import { classInstance } from '@/shared/helpers/helpers';
import { Counter } from '@/shared/counter/counter';

/** Событие внешней синхронизации количества (например, после o2k:basket:externalBasketDataUpdate). */
export const ADD_TO_CART_SYNC_EVENT = 'addToCart:sync' as const;

export type AddToCartSyncDetail = { value: number };

export function dispatchAddToCartSync(container: HTMLElement, quantityInCart: number): void {
	container.dispatchEvent(
		new CustomEvent<AddToCartSyncDetail>(ADD_TO_CART_SYNC_EVENT, {
			detail: { value: quantityInCart },
			bubbles: false,
		}),
	);
}

export class AddToCart {
	private container: HTMLElement;
	private button: HTMLButtonElement;
	private counterWrapper: HTMLElement;
	private decButton: HTMLButtonElement | null = null;
	private input: HTMLInputElement | null = null;
	private counterInstance: Counter | null = null;
	private active: boolean = false;

	constructor(element: HTMLElement) {
		if (!element) return;

		this.container = element;
		const existing = classInstance.get(this.container);
		if (existing?.addToCart) return;

		this.button = this.container.querySelector('.js-add-to-cart__button');
		this.counterWrapper = this.container.querySelector('.js-add-to-cart__counter');
		this.input = this.container.querySelector('.counter__input');
		this.active = this.container.classList.contains('add-to-cart--active');

		this.init();
		this.updateMinValueState();
	}

	private init = (): void => {
		if (!this.button || !this.counterWrapper) return;

		classInstance.set(this.container, { addToCart: this });

		this.counterInstance = new Counter(this.counterWrapper);
		this.decButton = this.counterWrapper.querySelector('.counter__button--dec');

		this.button.addEventListener('click', this.toggleHandler);
		this.counterWrapper.querySelector('.counter__input')?.addEventListener('change', this.handleCounterChange);

		this.container.addEventListener(ADD_TO_CART_SYNC_EVENT, this.handleSync);
	};

	public sync(value: number, withoutEvent: boolean = false): void {
		if (!this.input) return;

		this.active = value > 0;
		this.container.classList.toggle('add-to-cart--active', this.active);

		if (value > 0) {
			this.input.value = String(value);
		} else {
			const min = this.input.min && parseInt(this.input.min, 10) > 0 ? this.input.min : '1';
			this.input.value = min;
		}

		if (!withoutEvent) this.input.dispatchEvent(new Event('input'));

		this.updateMinValueState();
	}

	private toggleHandler = (): void => {
		if (this.active) {
			this.removeFromCart();
		} else {
			this.addToCart();
		}
	};

	private addToCart = (): void => {
		this.active = true;
		this.container.classList.add('add-to-cart--active');

		const input = this.counterWrapper.querySelector<HTMLInputElement>('.counter__input');
		if (input && (!input.value || parseInt(input.value, 10) <= 0)) {
			input.value = input.min || '1';
			input.dispatchEvent(new Event('input'));
		}

		this.container.dispatchEvent(new CustomEvent('append', { detail: { value: this.counterValue } }));
	};

	private removeFromCart = (): void => {
		this.active = false;
		this.container.classList.remove('add-to-cart--active');
		this.container.dispatchEvent(new CustomEvent('remove', { detail: { value: this.counterValue } }));
	};

	private handleCounterChange = (): void => {
		const value = this.counterValue;

		this.updateMinValueState();

		if (value <= 0) {
			this.removeFromCart();
		} else {
			this.container.dispatchEvent(new CustomEvent('change', { detail: { value } }));
		}
	};

	private updateMinValueState = (): void => {
		if (!this.decButton) return;

		const isMin = this.counterValue === 1;
		this.decButton.classList.toggle('counter__button--min-value', isMin);
	};

	private get counterValue(): number {
		const input = this.counterWrapper.querySelector<HTMLInputElement>('.counter__input');
		return parseInt(input?.value ?? '0', 10);
	}

	private handleSync = (e: CustomEvent<AddToCartSyncDetail>): void => {
		this.sync(typeof e.detail?.value === 'number' ? e.detail.value : 0);
	};

	destroy = (): void => {
		this.button.removeEventListener('click', this.toggleHandler);
		this.counterWrapper.querySelector('.counter__input')?.removeEventListener('change', this.handleCounterChange);
		this.container.removeEventListener(ADD_TO_CART_SYNC_EVENT, this.handleSync);
		classInstance.del(this.container, 'addToCart');
	};
}
